import { ConstraintType, PermissionGrantsType } from './PermissionGrants.types';

/**
 * @description Groups PermissionGrants by permission name and merges their associated Constraints.
 *
 * This function processes a list of PermissionGrant objects, where each grant
 * can contain a list of constraints.
 *
 * If multiple grants refer to the same permission, their constraints
 * are aggregated into a single list.
 *
 * Please see `PermissionGrants.test.ts` to see examples.
 *
 */
export const groupAndMergePermissionConstraints = (
    grants: PermissionGrantsType
): Map<string, Array<ConstraintType>> => {
    const outputMap: Map<string, Array<ConstraintType>> = new Map();

    for (const grant of grants) {
        const { permission, constraints } = grant;

        const existingConstraints = outputMap.get(permission);

        if (existingConstraints === undefined) {
            outputMap.set(permission, constraints);
            continue;
        }

        for (const constraint of constraints) {
            const foundIndex = existingConstraints.findIndex(
                (val) => val.field === constraint.field && val.type === constraint.type
            );

            if (foundIndex > -1) {
                const existing = existingConstraints[foundIndex];

                if (existing === undefined) {
                    continue;
                }

                if (existing.values !== undefined && constraint.values !== undefined) {
                    const mergedValues = new Set([...existing.values, ...constraint.values]);
                    existing.values = Array.from(mergedValues);
                } else {
                    if (JSON.stringify(existing) !== JSON.stringify(constraint)) {
                        existingConstraints.push(constraint);
                    }
                }
            } else {
                existingConstraints.push(constraint);
            }
        }
    }
    return outputMap;
};
